
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Lightbulb, X, Sparkles, Wifi, WifiOff } from 'lucide-react';
import { QuestionDisplay } from '@/components/guidance/QuestionDisplay';
import { SuggestionsList } from '@/components/guidance/SuggestionsList';
import { Suggestion } from '@/utils/questionHelpers';
import { useRealtimeGuidance } from '@/hooks/useRealtimeGuidance';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';

interface GuidancePanelProps {
  questions: string[];
  notes: Array<{ type: string; content: string }>;
  isOpen: boolean;
  onClose: () => void;
}

interface EnhancedGuidancePanelProps {
  big3Questions?: string[];
  backupQuestions?: string[];
  questions: string[]; // Legacy support
  notes: Array<{ type: string; content: string }>;
  isOpen: boolean;
  onClose: () => void;
  noteSpaceId?: string;
}

export const GuidancePanel = ({ questions, notes, isOpen, onClose, noteSpaceId, big3Questions, backupQuestions }: EnhancedGuidancePanelProps) => {
  const isLiveGuidanceAvailable = useFeatureFlag('enableLiveGuidance');
  const isRealtimeEnabled = useFeatureFlag('enableRealtimeGuidance');

  const {
    suggestions,
    isLoading,
    isConnected,
    error,
    requestGuidance,
    updateContext,
    streamingContent
  } = useRealtimeGuidance(noteSpaceId);

  // Extract Big 3 and backup from legacy questions or use provided ones
  const derivedBig3 = big3Questions || questions.slice(0, 3);
  const derivedBackup = backupQuestions || questions.slice(3);

  // Update context when questions or notes change
  useEffect(() => {
    // Ensure questions is an array and filter out invalid entries
    const validBig3 = Array.isArray(derivedBig3) 
      ? derivedBig3.filter(q => q && typeof q === 'string' && q.trim().length > 0)
      : [];
    const validBackup = Array.isArray(derivedBackup) 
      ? derivedBackup.filter(q => q && typeof q === 'string' && q.trim().length > 0)
      : [];

    // Ensure notes is an array and filter out invalid entries
    const validNotes = Array.isArray(notes)
      ? notes.filter(note => note && typeof note.content === 'string' && note.content.trim().length > 0)
      : [];

    // Combine all questions for context
    const allQuestions = [...validBig3, ...validBackup];
    
    if (allQuestions.length > 0) {
      // Determine current stage based on notes count
      let currentStage: 'early' | 'middle' | 'late' = 'early';
      if (validNotes.length > 10) currentStage = 'late';
      else if (validNotes.length > 3) currentStage = 'middle';

      updateContext(allQuestions, validNotes, currentStage);
      requestGuidance();
    }
  }, [derivedBig3, derivedBackup, notes, updateContext, requestGuidance]);

  // Don't render at all if feature flag is disabled
  if (!isLiveGuidanceAvailable) {
    return null;
  }

  if (!isOpen) return null;

  // Safely get valid questions for display
  const validBig3Display = Array.isArray(derivedBig3) 
    ? derivedBig3.filter(q => q && typeof q === 'string' && q.trim().length > 0)
    : [];
  const validBackupDisplay = Array.isArray(derivedBackup) 
    ? derivedBackup.filter(q => q && typeof q === 'string' && q.trim().length > 0)
    : [];

  return (
    <div className="fixed right-0 top-0 h-full w-80 bg-white border-l border-gray-200 shadow-xl flex flex-col z-50">
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
            <h2 className="text-lg font-semibold text-gray-900">Live Guidance</h2>
            
            {isRealtimeEnabled && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                {isConnected ? (
                  <><Wifi className="h-3 w-3 text-green-500" /> Realtime</>
                ) : (
                  <><WifiOff className="h-3 w-3 text-red-500" /> Offline</>
                )}
              </div>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Big 3 Questions and Backup Questions */}
      <QuestionDisplay
        validBig3Display={validBig3Display}
        validBackupDisplay={validBackupDisplay}
      />

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-3">
          <h3 className="text-sm font-medium text-gray-900 flex items-center space-x-2">
            <Lightbulb className="w-4 h-4 text-amber-500" />
            <span>Suggestions</span>
            <Badge variant="secondary" className="text-xs">{suggestions.length}</Badge>
          </h3>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
          
          {isLoading ? (
            <div className="space-y-3">
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-amber-500"></div>
                <span className="ml-2 text-sm text-gray-500">
                  {isRealtimeEnabled ? 'Generating guidance...' : 'Loading suggestions...'}
                </span>
              </div>
              
              {streamingContent && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-xs text-blue-600 font-mono whitespace-pre-wrap">
                    {streamingContent}
                  </p>
                </div>
              )}
            </div>
          ) : suggestions.length > 0 ? (
            <SuggestionsList suggestions={suggestions} />
          ) : (
            <p className="text-sm text-gray-500 py-4">
              No suggestions available. Start asking questions to get guidance.
            </p>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};
